import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import AuthLayout from "../layouts/AuthLayout.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import apiClient from "../services/api.js";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpDebug, setOtpDebug] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const passwordChecks = useMemo(
    () => ({
      length: form.password.length >= 8,
      number: /\d/.test(form.password),
      letter: /[A-Za-z]/.test(form.password)
    }),
    [form.password]
  );
  const passwordReady = Object.values(passwordChecks).every(Boolean);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!passwordReady) {
      setError("Use at least 8 characters with a letter and a number.");
      return;
    }
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { confirm_password: _confirmPassword, ...registrationFields } = form;
      const payload = {
        ...registrationFields,
        first_name: registrationFields.first_name.trim(),
        last_name: registrationFields.last_name.trim(),
        email: registrationFields.email.trim().toLowerCase(),
        phone: registrationFields.phone.trim()
      };
      const { data } = await apiClient.post("/auth/register", payload);
      if (data?.access_token) {
        login(data);
        navigate("/dashboard");
        return;
      }
      if (data?.otp_debug) {
        setOtpDebug(data.otp_debug);
      }
      navigate(`/verify?email=${encodeURIComponent(payload.email)}`, {
        state: { fromRegistration: true }
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Join the cooperative"
      subtitle="Set up your secure member profile. We’ll verify your email before your first sign-in."
      steps={["Account", "Verify", "Sign in"]}
      currentStep={1}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <span>Already have an account?</span>
          <Link className="font-semibold text-accent" to="/login">
            Sign in
          </Link>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              First name
            </label>
            <input
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              className="input mt-2"
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              Last name
            </label>
            <input
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              className="input mt-2"
              autoComplete="family-name"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="input mt-2"
            autoComplete="email"
            placeholder="you@example.com"
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Phone
          </label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="input mt-2"
            autoComplete="tel"
            placeholder="+234"
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className="text-xs font-semibold text-accent"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={form.password}
            onChange={handleChange}
            className="input mt-2"
            autoComplete="new-password"
            minLength={8}
            required
          />
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            {[
              ["8+ characters", passwordChecks.length],
              ["One letter", passwordChecks.letter],
              ["One number", passwordChecks.number]
            ].map(([label, complete]) => (
              <span
                key={label}
                className={`rounded-full px-3 py-1 ${
                  complete ? "bg-mint/15 text-emerald-700" : "bg-black/[0.04] text-slate-500"
                }`}
              >
                {complete ? "✓ " : ""}
                {label}
              </span>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
            Confirm password
          </label>
          <input
            type={showPassword ? "text" : "password"}
            name="confirm_password"
            value={form.confirm_password}
            onChange={handleChange}
            className="input mt-2"
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        {error && (
          <p role="alert" className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </p>
        )}
        {otpDebug && (
          <p className="rounded-2xl bg-white/70 p-3 text-xs text-slate-600">
            OTP debug code: <span className="font-semibold text-accent">{otpDebug}</span>
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-2xl bg-accent px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-accent/90"
        >
          {loading ? "Creating..." : "Create account"}
        </button>
        <p className="text-center text-[11px] leading-5 text-slate-500">
          By continuing, you confirm that the information provided belongs to you.
        </p>
      </form>
    </AuthLayout>
  );
}
